/**
 * ProductHeader (S1.4) — detail header card on the VendorDetailPage trio
 * pattern: back + breadcrumb (主資料 › 產品 › name), Internal Product ID
 * prominent (primary identity), readiness banner, active Switch,
 * TimestampPair.
 *
 * S1.2 — sits above the five-tab rail; the SCN meta chip stays (it becomes a
 * link to the SCN Spec Master in W7). The readiness Alert is tab-scoped: off on
 * 基本資料 (MainInfoSection's ReadinessMeter states the gaps there), on
 * elsewhere — the gaps stay visible exactly once per view.
 */

import { Alert, Breadcrumb, Button, Card, Divider, Switch, Tag, Tooltip, theme } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  EditOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StatusTag from '@/components/common/StatusTag';
import PurpleTag from '@/components/common/PurpleTag';
import SourceStampTag from '@/components/common/SourceStampTag';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import { useItemLocations } from '@/hooks/useItemLocations';
import { controlHeightTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import type { Product, ProductReadinessGap } from '@/types/api/product';

const GAP_LABEL_KEYS: Record<ProductReadinessGap, string> = {
  descriptions: 'products:wizard.reviewMeter.groups.descriptions',
  pricingRef: 'products:wizard.reviewMeter.groups.pricingRef',
  costingRef: 'products:wizard.reviewMeter.groups.costingRef',
};

interface ProductHeaderProps {
  product: Product;
  toggling: boolean;
  /**
   * Render the readiness-gap Alert. Off on the 基本資料 tab, where
   * MainInfoSection's ReadinessMeter already states the gaps (avoids the
   * duplicated signal); on for every other tab so the gaps stay visible
   * exactly once per view.
   */
  showReadinessAlert?: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
}

export const ProductHeader = ({
  product,
  toggling,
  showReadinessAlert = true,
  onEdit,
  onToggleStatus,
}: ProductHeaderProps) => {
  const { t } = useTranslation(['products', 'common']);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const elevation = useElevation();

  // SR4.1 — 可供量@syncedAt read-through chip. A Tier-1 NetSuite echo (never
  // owned here); shares its cacheKey with ItemLocationsSection so opening that
  // section reuses this fetch. Hidden until a mapped item + snapshot resolve.
  const { totals, netSuiteItemId, syncedAt } = useItemLocations(product.id);

  const ready = product.readiness?.ready ?? false;
  const missingLabels = (product.readiness?.missing ?? []).map((gap) => t(GAP_LABEL_KEYS[gap]));

  const metaItem = (label: string, value: React.ReactNode) => (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>{label}</span>
      <span style={{ fontSize: token.fontSize, fontWeight: token.fontWeightStrong }}>{value}</span>
    </div>
  );

  return (
    <Card
      className="tw-mb-6"
      style={{
        ...elevation.card,
      }}
    >
      {/* Back + breadcrumb */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/auth/products')}
          style={{ padding: 0, height: 'auto', lineHeight: 1 }}
        >
          {t('products:backToProducts')}
        </Button>
        <Breadcrumb
          items={[
            { title: t('common:masterData') },
            {
              title: <a onClick={() => navigate('/auth/products')}>{t('products:listTitle')}</a>,
            },
            { title: product.displayName },
          ]}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      {/* Title + actions */}
      <div className="tw-flex tw-justify-between tw-items-start tw-gap-6 tw-mb-4 tw-flex-wrap">
        <div className="tw-flex-1" style={{ minWidth: 0 }}>
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <h1
              style={{
                fontSize: token.fontSizeHeading3,
                fontWeight: token.fontWeightStrong,
                color: token.colorPrimary,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              {product.displayName}
            </h1>
            <Tooltip title={ready ? undefined : missingLabels.join(' · ')}>
              <span>
                <StatusTag
                  status={ready ? 'active' : 'pending'}
                  label={t(ready ? 'products:readiness.ready' : 'products:readiness.blocked')}
                />
              </span>
            </Tooltip>
            {/* SR0.2 — 禁止採購 restriction (tone + icon + label, not colour-only). */}
            {product.planning?.doNotOrder && (
              <Tag color="error" icon={<StopOutlined />} style={{ margin: 0 }}>
                {t('products:planning.doNotOrderTag')}
              </Tag>
            )}
          </div>
          {product.nameZh && (
            <p
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextTertiary,
                margin: '4px 0 0 0',
              }}
            >
              {product.nameZh}
            </p>
          )}
        </div>

        <Button
          type="primary"
          icon={<EditOutlined />}
          disabled={!product.isActive}
          onClick={onEdit}
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
        >
          {t('common:actions.edit')}
        </Button>
      </div>

      {!product.isActive && (
        <Alert className="tw-mb-4" type="warning" showIcon message={t('products:inactiveBanner')} />
      )}

      {!ready && showReadinessAlert && (
        <Alert
          className="tw-mb-4"
          type="warning"
          showIcon
          message={t('products:readiness.blockedTitle')}
          description={t('products:readiness.blockedDescription', {
            fields: missingLabels.join('、'),
          })}
        />
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* Meta row — Internal Product ID prominent (primary identity) */}
      <div className="tw-flex tw-gap-8 tw-flex-wrap tw-items-center" style={{ paddingTop: '8px' }}>
        <div className="tw-flex tw-items-center tw-gap-2">
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {t('products:fields.internalProductId')}
          </span>
          <span
            style={{
              fontSize: token.fontSizeLG,
              fontWeight: token.fontWeightStrong,
              color: token.colorPrimary,
            }}
          >
            {product.internalProductId}
          </span>
        </div>
        {metaItem(t('products:fields.sku'), product.sku)}
        {metaItem(
          t('products:fields.scn'),
          product.scn ? <PurpleTag>{product.scn}</PurpleTag> : t('products:notAssigned')
        )}
        {/* SR4.1 — 可供量@syncedAt read-through chip (Tier-1 echo, never owned).
            Names the NetSuite source alongside the value (parity with
            ItemLocationsSection) so the two-tier boundary is signalled here too. */}
        {netSuiteItemId && syncedAt && totals && (
          <SourceStampTag
            sourceLabel={`${t('products:itemLocations.source')} · ${t(
              'products:itemLocations.columns.availableQty'
            )} ${totals.availableQty.toLocaleString()}`}
            syncedAt={syncedAt}
          />
        )}
        <TimestampPair
          createdAt={product.createdAt}
          updatedAt={product.updatedAt}
          direction="horizontal"
        />
        <div className="tw-flex tw-items-center tw-gap-2">
          <CheckCircleOutlined
            style={{ fontSize: token.fontSize, color: token.colorTextSecondary }}
          />
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {t('common:status.title')}
          </span>
          <span style={{ fontSize: token.fontSize, fontWeight: token.fontWeightStrong }}>
            {t(product.isActive ? 'common:status.active' : 'common:status.inactive')}
          </span>
          {/* R1 (Q13 M1) — accessible name only. The wizard twin
              (`ProductFormWizard.tsx`) already carries this exact label; the
              detail header did not, so the control announced as an unnamed
              switch. NOT a control-type change: the Q13 Switch-vs-Radio canon
              is HELD for its owner and nothing here converts a control. */}
          <Switch
            checked={product.isActive}
            onChange={onToggleStatus}
            loading={toggling}
            aria-label={t('products:fields.isActive')}
            style={{ transform: 'scale(0.8)' }}
          />
        </div>
      </div>
    </Card>
  );
};

export default ProductHeader;
